import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Helmet } from "react-helmet";
import { LuArrowRight, LuArrowLeft, LuClock, LuUser } from "react-icons/lu";
import HeaderOne from "../../header/HeaderOne";
import FooterOne from "../../footer/FooterOne";
import Head from "../../components/Head";
import "./BlogPost.css";

const SITE_URL = "https://textlinksms.com";

const estimateReadTime = (text) => {
  if (!text) return 3;
  const words = String(text).split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const res = await fetch(`${global.SERVIP}/blog-controller/${slug}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setBlog(data.blog || data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecommended = async () => {
      try {
        const res = await fetch(`${global.SERVIP}/blog-controller/recent/get`);
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.blogs || [];
        setRecommended(list);
      } catch {
        /* no-op — recommended is non-critical */
      }
    };

    fetchBlog();
    fetchRecommended();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <>
        <HeaderOne />
        <main className="bp">
          <div className="bp-shell">
            <p className="bp-state">Loading…</p>
          </div>
        </main>
        <FooterOne />
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Head title="Guide not found — TextLink" />
        <HeaderOne />
        <main className="bp">
          <div className="bp-shell">
            <header className="bp-hero">
              <h1 className="bp-h1">Guide not found</h1>
              <p className="bp-lead">
                The page you're looking for doesn't exist or may have been moved.
              </p>
              <Link to="/guides" className="bp-back">
                <LuArrowLeft size={14} strokeWidth={2} /> Back to all guides
              </Link>
            </header>
          </div>
        </main>
        <FooterOne />
      </>
    );
  }

  const {
    title, subtitle, excerpt, coverImage, category, content,
    createdAt, writer, writerImg, writerDesc
  } = blog;

  const minutes = estimateReadTime(content);
  const filteredRecommended = (recommended || [])
    .filter((p) => p && p.slug && p.slug !== slug)
    .slice(0, 3);

  // Article structured data — eligible for rich snippets in Google.
  // datePublished/dateModified come from the DB even though we don't
  // surface them in the UI; Google still uses them for ranking signals.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": subtitle || excerpt || undefined,
    "image": coverImage ? [coverImage] : [`${SITE_URL}/assets/img/logo/logo2.png`],
    "datePublished": createdAt,
    "dateModified": blog.updatedAt || createdAt,
    "author": {
      "@type": "Person",
      "name": writer || "TextLink"
    },
    "publisher": {
      "@type": "Organization",
      "name": "TextLink",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/assets/img/logo/logo2.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/${slug}`
    }
  };

  return (
    <>
      <Head
        title={title}
        description={subtitle || excerpt || undefined}
        image={coverImage || undefined}
        canonical={`/${slug}`}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>
      <HeaderOne />

      <main className="bp">
        <article className="bp-shell">

          {/* ---- Back link ---- */}
          <div className="bp-topnav">
            <Link to="/guides" className="bp-back">
              <LuArrowLeft size={14} strokeWidth={2} /> Guides
            </Link>
          </div>

          {/* ---- Hero ---- */}
          <header className="bp-hero">
            {category && <span className="bp-eyebrow">{category}</span>}
            <h1 className="bp-h1">{title}</h1>
            {subtitle && <p className="bp-lead">{subtitle}</p>}

            <div className="bp-meta">
              {writer && (
                <span className="bp-meta-item">
                  <LuUser size={13} strokeWidth={1.75} /> {writer}
                </span>
              )}
              <span className="bp-meta-item">
                <LuClock size={13} strokeWidth={1.75} /> {minutes} min read
              </span>
            </div>
          </header>

          {/* ---- Cover image (optional) ---- */}
          {coverImage && (
            <figure className="bp-cover">
              <img src={coverImage} alt="" />
            </figure>
          )}

          {/* ---- Article body ---- */}
          {content && (
            <div className="bp-body">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a
                      {...props}
                      target={props.href?.startsWith('http') ? '_blank' : undefined}
                      rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    />
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}

          {/* ---- Writer card ---- */}
          {(writer || writerDesc) && (
            <aside className="bp-writer">
              {writerImg && (
                <img src={writerImg} alt={writer || ''} className="bp-writer-img" />
              )}
              <div className="bp-writer-body">
                {writer && <h3 className="bp-writer-name">{writer}</h3>}
                {writerDesc && <p className="bp-writer-desc">{writerDesc}</p>}
              </div>
            </aside>
          )}

          {/* ---- CTA ---- */}
          <section className="bp-cta">
            <h2 className="bp-cta-title">Ready to send your first SMS?</h2>
            <p className="bp-cta-text">
              Five minutes from signup to first message. 300 free messages, no credit card required.
            </p>
            <Link to="/signup" className="bp-btn">
              Try TextLink free <LuArrowRight size={15} strokeWidth={2} />
            </Link>
          </section>

          {/* ---- Recommended ---- */}
          {filteredRecommended.length > 0 && (
            <section className="bp-recommended">
              <h2 className="bp-rec-title">Read next</h2>
              <div className="bp-rec-grid">
                {filteredRecommended.map((p) => (
                  <Link
                    key={p.id}
                    to={`/${p.slug}`}
                    className="bp-rec-card"
                  >
                    {p.category && <span className="bp-rec-cat">{p.category}</span>}
                    <h3 className="bp-rec-card-title">{p.title}</h3>
                    {p.subtitle && <p className="bp-rec-card-sub">{p.subtitle}</p>}
                  </Link>
                ))}
              </div>
            </section>
          )}

        </article>
      </main>

      <FooterOne />
    </>
  );
}
